import React, { useEffect, useRef } from 'react';
import { heroTemplates, demoStoreUrl, templatesForVisitor } from './Hero';
import useDemoThemes from '../utils/demoThemes';
import './StoreDesigns.css';
import { trackGetFreeClick } from '../utils/track';
import PreviewImage from './PreviewImage';
import { useOwnezzyContact, whatsappLink } from '../legal/contact';
import { openCustomWebsiteModal } from '../utils/customModal';

// How wide a card's screenshot is shown (StoreDesigns.css: one column on phones, at most ~370px otherwise)
const PREVIEW_SIZES = '(max-width: 575px) calc(100vw - 58px), 370px';

// Shop types we are preparing designs for, listed on the "coming soon" card
const COMING_SOON = ['🍰 Custom Bakery', '👶 Baby & Kids', '🕶️ Eyewear & Optics', '✨ And many more'];
const REQUEST_MESSAGE = 'Hi, I would like an Ownezzy website for my shop. My shop sells: ';

// "Choose the Website": the designs that have a live demo store, each with a screenshot of the demo (preview),
// what a seller can sell with it (pitch) and a link that opens the demo, where "Get this website for free" signs up.
// Cards rise into view as the list scrolls in, one after another.
export default function StoreDesigns() {
  const contact = useOwnezzyContact();
  const designs = useDemoThemes(templatesForVisitor(heroTemplates)).filter((t) => t.demo); // international stores first abroad
  const gridRef = useRef(null);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;
    const cards = [...grid.children];
    if (!('IntersectionObserver' in window)) {
      cards.forEach((card) => card.classList.add('sd-in'));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('sd-in');
          observer.unobserve(entry.target);
        }
      }),
      { rootMargin: '0px 0px -60px 0px', threshold: 0.12 }
    );
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="sd-section" id="select-website">
      <div className="container sd-container">
        <div className="section-heading text-center">
          <span className="section-kicker">100+ READY TO USE STORE WEBSITES BY OWNEZZY</span>
          <h2>Choose a website for your shop & business</h2>
          <p>Explore live demo stores loaded with 30 real products, 0% commission, and direct UPI checkout. Get the same website for your store in 60 seconds.</p>
        </div>
        <div className="sd-grid" ref={gridRef}>
          {designs.map((t, index) => {
            const url = demoStoreUrl(t);
            return (
              <a
                className="sd-card"
                key={t.id}
                href={url}
                target="_blank"
                rel="noopener"
                onClick={trackGetFreeClick}
                style={{ '--sd-color': t.theme && t.theme.main, '--sd-soft': t.theme && t.theme.soft, '--sd-delay': `${(index % 3) * 0.1}s` }}
              >
                <span className="sd-shot">
                  <span className="sd-frame">
                    <span className="sd-bar" aria-hidden="true"><i /><i /><i /><span>{t.demo}.ownezzy.com</span></span>
                    <PreviewImage template={t} sizes={PREVIEW_SIZES} alt={`${t.name} - ${t.business || t.label} online store website with UPI payment`} width="960" height="984" loading="lazy" />
                  </span>
                  <span className="sd-tag"><span className="sd-tag-icon" aria-hidden="true">{t.icon}</span>{t.label}</span>
                  <span className="sd-live" aria-hidden="true"><i /> Live demo</span>
                </span>
                <span className="sd-body">
                  <b className="sd-name">{t.name}</b>
                  <span className="sd-pitch">{t.pitch}</span>
                  <span className="sd-go">
                    Get it now <span className="sd-go-arrow" aria-hidden="true">→</span>
                  </span>
                </span>
              </a>
            );
          })}

          <div className="sd-card sd-soon" style={{ '--sd-delay': `${(designs.length % 3) * 0.1}s` }}>
            <span className="sd-soon-glow" aria-hidden="true" />
            <span className="sd-soon-badge"><i aria-hidden="true" /> In the works</span>
            <span className="sd-soon-icon" aria-hidden="true">
              <span className="sd-soon-ring" />
              <span className="sd-soon-emoji">✨</span>
            </span>
            <b className="sd-name">More designs coming soon</b>
            <span className="sd-note">New ready-made websites for more kinds of shops are on the way.</span>
            <span className="sd-soon-list">
              {COMING_SOON.map((item, i) => <span key={item} style={{ '--sd-i': i }}>{item}</span>)}
            </span>
            <div className="sd-soon-actions">
              <button
                type="button"
                className="sd-soon-custom-btn"
                onClick={() => openCustomWebsiteModal()}
              >
                ✨ Create Custom Website
              </button>
              <a className="sd-soon-link" href={whatsappLink(contact, REQUEST_MESSAGE)} target="_blank" rel="noopener noreferrer">
                <i className="bi bi-whatsapp" aria-hidden="true" /> Tell us what you sell
              </a>
            </div>
          </div>
        </div>
        <div className="sd-view-all-cta text-center" style={{ marginTop: '36px' }}>
          <a href="/websites" className="sd-view-all-btn">
            <span>Browse All 100+ Ready-Made Websites with Filters</span>
            <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    </section>
  );
}
